import { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

/**
 * Pick a department/position by NAME from the org structure (SoT: /owner/org).
 * - has options → dropdown; a current value not in the list is preserved as a
 *   "(เดิม)" option so legacy/free-typed data never silently blanks on save.
 * - no options yet → free text input + hint (don't block adding an employee
 *   before the org is set up).
 */
const ADD_NEW = '__add_new__';
export function OrgNameSelect({
  value, onChange, options, inUse = [], placeholder, emptyHint, onAdd, addLabel, onPendingChange,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  /** ชื่อที่พนักงานคนอื่นใช้อยู่แต่ยังไม่อยู่ในโครงสร้าง — เลือกแล้วลงทะเบียนเข้าโครงสร้างให้ */
  inUse?: string[];
  placeholder: string;
  emptyHint: string;
  /** สร้างค่าใหม่เข้าโครงสร้างองค์กร แล้วคืนชื่อที่บันทึก (ใช้ผูกพนักงาน) */
  onAdd?: (name: string) => Promise<string>;
  addLabel?: string;
  /** มีชื่อใหม่ที่พิมพ์ค้างไว้แต่ยังไม่กด ✓ หรือกำลังบันทึก — ฟอร์มที่ส่งค่าทันทีต้องรอ (ค่าที่ส่งยังเป็นค่าเดิม) */
  onPendingChange?: (pending: boolean) => void;
}) {
  // โหมดเพิ่มใหม่ — เปิดเมื่อเลือก "+ เพิ่มใหม่" หรือยังไม่มีตัวเลือกเลย
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inAddMode = !!onAdd && (adding || options.length === 0);
  const pending = saving || (inAddMode && !!newName.trim());
  useEffect(() => { onPendingChange?.(pending); }, [pending, onPendingChange]);

  async function commitAdd() {
    const name = newName.trim();
    if (!name || !onAdd) return;
    setSaving(true);
    setErr(null);
    try {
      const saved = await onAdd(name);
      onChange(saved);
      setAdding(false);
      setNewName('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'เพิ่มไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  // โหมดกรอกชื่อใหม่ (inline)
  if (onAdd && (adding || options.length === 0)) {
    return (
      <div>
        <div className="flex gap-1">
          <input
            className="input flex-1"
            autoFocus
            placeholder={addLabel ?? 'พิมพ์ชื่อใหม่'}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitAdd(); } }}
          />
          <button type="button" onClick={commitAdd} disabled={saving || !newName.trim()} className="btn-primary px-2 shrink-0" title="บันทึกเข้าโครงสร้าง">
            <Check className="w-4 h-4" />
          </button>
          {options.length > 0 && (
            <button type="button" onClick={() => { setAdding(false); setNewName(''); setErr(null); }} disabled={saving} className="btn-secondary px-2 shrink-0" title="ยกเลิก">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          {saving ? 'กำลังบันทึกเข้าโครงสร้างองค์กร…' : `${addLabel ?? 'เพิ่มใหม่'} แล้วผูกเข้าโครงสร้างองค์กรให้อัตโนมัติ`}
        </p>
        {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
      </div>
    );
  }

  // ไม่มี onAdd และไม่มีตัวเลือก → free text เดิม (เผื่อเคสไม่ส่ง onAdd)
  if (options.length === 0) {
    return (
      <>
        <input className="input" value={value} onChange={(e) => onChange(e.target.value)} />
        <p className="text-[11px] text-gray-400 mt-1">{emptyHint}</p>
      </>
    );
  }

  const legacy = inUse.filter((n) => !options.includes(n));
  const inList = !value || options.includes(value) || legacy.includes(value);
  async function pickLegacy(name: string) {
    onChange(name);
    if (!onAdd) return;
    setSaving(true);
    setErr(null);
    try {
      onChange(await onAdd(name));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'ลงทะเบียนเข้าโครงสร้างไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
    <select
      className="input"
      value={value}
      disabled={saving}
      onChange={(e) => {
        if (e.target.value === ADD_NEW) { setAdding(true); setNewName(''); setErr(null); return; }
        if (legacy.includes(e.target.value)) { void pickLegacy(e.target.value); return; }
        onChange(e.target.value);
      }}
    >
      <option value="">{placeholder}</option>
      {!inList && <option value={value}>{value} (เดิม)</option>}
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
      {legacy.length > 0 && (
        <optgroup label="ใช้อยู่กับพนักงานคนอื่น (ยังไม่อยู่ในโครงสร้างองค์กร)">
          {legacy.map((o) => (
            <option key={`legacy-${o}`} value={o}>{o}</option>
          ))}
        </optgroup>
      )}
      {onAdd && <option value={ADD_NEW}>+ {addLabel ?? 'เพิ่มใหม่'}…</option>}
    </select>
    {saving && <p className="text-[11px] text-gray-400 mt-1">กำลังบันทึกเข้าโครงสร้างองค์กร…</p>}
    {err && <p className="text-[11px] text-red-600 mt-1">{err}</p>}
    </>
  );
}
